import Seo from "../components/Seo";
import LeadForm from "../components/LeadForm";
import FAQ from "../components/FAQ";
import ServiceCTA from "../components/service/ServiceCTA";
import { KineticLines, Reveal, SectionLabel } from "../components/Reveal";
import { HeartPulse, MonitorSmartphone, Eye, Pill, ClipboardCheck, HandHeart } from "lucide-react";
import { ArrowRight } from "lucide-react";
import { Info } from "lucide-react";

const OPTIONS = [
  { icon: HeartPulse, title: "Healthcare coverage options", copy: "Explore coverage approaches that may fit your team and your budget." },
  { icon: HandHeart, title: "Employee benefit choices", copy: "Meaningful benefits that help people feel protected — and valued." },
  { icon: MonitorSmartphone, title: "Virtual care", copy: "Convenient access to care that doesn't require a waiting room." },
  { icon: Eye, title: "Dental & vision options", copy: "The benefits people actually use, available as part of the picture." },
  { icon: Pill, title: "Prescription savings programs", copy: "Programs designed to ease one of the most common household costs." },
  { icon: ClipboardCheck, title: "Employer enrollment support", copy: "Guidance through the paperwork so choosing benefits feels manageable." },
];

const FAQS = [
  { q: "Is this health insurance?", a: "Not exactly. Depending on your business, options may include healthcare coverage arrangements as well as alternatives to traditional health insurance — such as virtual care and benefit programs. Specialists explain what each option is, and what it is not, so you can decide with confidence." },
  { q: "What kinds of benefits can we explore?", a: "Healthcare coverage options, employee benefit choices, virtual care, dental and vision options, prescription savings programs, and employer enrollment support." },
  { q: "Will every option be available to my team?", a: "Availability depends on your business, your team, and your location — not every option fits every business. A specialist can help identify which choices may be available to you." },
  { q: "Are we required to offer everything to employees?", a: "No. Many options are flexible. You explore what exists, and you decide what makes sense for your team and your budget." },
];

function BenefitsViz() {
  const people = [{ x: 70, y: 70 }, { x: 70, y: 180 }, { x: 180, y: 125 }];
  return (
    <div className="relative rounded-3xl bg-white border border-line shadow-core p-6 md:p-8" data-testid="benefits-viz">
      <svg viewBox="0 0 440 260" className="w-full h-auto" role="img" aria-label="Connected employees within a protective benefits shield">
        {people.map((p, i) => (
          <g key={i} style={{ animation: `floatY ${5 + i}s ease-in-out infinite`, animationDelay: `${i * 0.5}s` }}>
            <path d={`M${p.x + 26} ${p.y} C 130 ${p.y}, 160 125, 218 125`} fill="none" stroke="#8FB3D9" strokeWidth="1.7" strokeDasharray="3 6" className="animate-dashFlow" style={{ animationDelay: `${i * 0.3}s` }} />
            <circle cx={p.x} cy={p.y - 10} r="13" fill="#234D85" opacity="0.92" />
            <path d={`M${p.x - 17} ${p.y + 30} C ${p.x - 17} ${p.y + 8}, ${p.x + 17} ${p.y + 8}, ${p.x + 17} ${p.y + 30} Z`} fill="#15B8A6" opacity="0.88" />
          </g>
        ))}
        <path d="M270 60 L350 32 L350 130 C 350 178, 314 206, 270 226 C 226 206, 190 178, 190 130 L190 32 Z" fill="#EAF4F8" stroke="#15B8A6" strokeWidth="2.2" />
        <path d="M244 130 L262 148 L298 104" stroke="#0F9D77" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="270" y="250" textAnchor="middle" fontSize="11" fontWeight="700" fill="#617083" style={{ fontFamily: "Inter, sans-serif" }}>Protection that reaches your whole team</text>
      </svg>
      <p className="mt-2 text-center text-xs font-semibold text-slate">People first — with options that fit real budgets.</p>
    </div>
  );
}

export default function HealthcareBenefits() {
  return (
    <main data-testid="page-healthcare-benefits">
      <Seo
        title="Employee Healthcare & Benefits"
        description="Explore healthcare coverage options, virtual care, dental and vision, and benefit choices for your team — connected with specialized resources."
        path="/healthcare-benefits"
      />

      <section className="relative overflow-hidden bg-ice pt-28 lg:pt-36 pb-16 md:pb-24">
        <div className="absolute inset-0 bg-arch" aria-hidden="true" />
        <div className="absolute -top-24 right-[10%] w-[460px] h-[460px] rounded-full bg-teal/10 blur-[110px]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="lg:order-2">
            <SectionLabel>Employee Healthcare & Benefits</SectionLabel>
            <KineticLines
              as="h1"
              className="mt-5 font-display font-extrabold tracking-tightest text-navy text-4xl sm:text-5xl leading-[1.06]"
              lines={["Take Better Care", <>of Your <span className="text-teal-dark">People.</span></>]}
            />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate">
                Explore healthcare and employee benefit options that may help your business offer meaningful choices
                while managing costs. From healthcare coverage options to virtual care and additional benefits, we can
                help you connect with specialized resources.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <a href="#benefits-form" data-testid="benefits-hero-cta" className="group mt-8 inline-flex items-center gap-2 bg-navy text-white font-bold text-[15px] px-7 py-4 rounded-full transition-all duration-300 hover:bg-teal hover:text-navy shadow-lift">
                Explore My Benefits Options
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
          <div className="lg:order-1"><Reveal delay={0.2}><BenefitsViz /></Reveal></div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>What You Can Explore</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["Benefits your people can feel — without dread at renewal."]} />
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {OPTIONS.map((o, i) => (
              <Reveal key={o.title} delay={i * 0.05} className="h-full">
                <div className="group h-full rounded-2xl border border-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift hover:border-teal/50" data-testid={`benefits-option-${i + 1}`}>
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-ice group-hover:bg-teal-soft transition-colors">
                    <o.icon className="w-5 h-5 text-royal group-hover:text-teal-dark transition-colors" />
                  </span>
                  <h3 className="mt-4 font-display font-extrabold tracking-tight text-navy text-lg">{o.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate">{o.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 rounded-2xl border border-royal/20 bg-ice p-6 flex items-start gap-4" data-testid="benefits-distinction-note">
              <Info className="w-5 h-5 text-royal shrink-0 mt-0.5" />
              <p className="text-sm md:text-[15px] leading-relaxed text-navy">
                <strong>A note on terminology.</strong> Depending on where your business operates, some options are
                healthcare <em>arrangements</em> rather than traditional health insurance. Specialists will clearly
                distinguish what each option is — and is not — before you consider anything.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ice/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <SectionLabel>Who This Helps</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl leading-[1.1]" lines={["For owners who want to do right by their team."]} />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate">
                Growing teams that need benefits for the first time. Established businesses watching healthcare costs
                climb year after year. Owners who've never had options explained plainly. If people are the heart of
                your business, benefits deserve a smarter look.
              </p>
            </Reveal>
          </div>
          <div>
            <SectionLabel>How The Process Works</SectionLabel>
            <div className="mt-6 space-y-6">
              {[
                ["Tell us about your team", "Size, make-up, and what matters most to your people."],
                ["We connect you", "You're introduced to specialists focused on healthcare and benefits for businesses like yours."],
                ["Compare with clarity", "Options, availability, and costs explained — you choose what fits."],
              ].map(([t, c], i) => (
                <Reveal key={t} delay={i * 0.08}>
                  <div className="flex gap-4">
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-navy text-teal-bright text-xs font-extrabold shrink-0">{i + 1}</span>
                    <div>
                      <h3 className="font-display font-extrabold text-navy">{t}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate">{c}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQ items={FAQS} title="Benefits Questions" label="FAQ" />

      <section id="benefits-form" className="py-16 md:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Explore Options</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["See what benefits could look like for your team."]} />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate max-w-lg">
                Tell us a little about your business and we'll help you connect with resources that specialize in
                employee healthcare and benefits.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}><LeadForm service="benefits" heading="Explore My Benefits Options" /></Reveal>
        </div>
      </section>

      <ServiceCTA
        service="benefits"
        title={["Better benefits begin", "with better information."]}
        copy="Tell us about your team and we'll help you connect with specialists who can walk you through the options."
        cta="Explore My Benefits Options"
      />
    </main>
  );
}
